import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  isRunningStandalone,
  PWA_DISMISSED_KEY,
  PwaInstallContext,
  type BeforeInstallPromptEvent,
  type PwaInstallValue,
} from './pwaInstall'

/** Storage can throw (Safari private mode, blocked cookies) — never let it break render. */
function readDismissed() {
  try {
    return sessionStorage.getItem(PWA_DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Dev-only escape hatch: `?pwaBanner=1` forces `canInstall` so the install
 * banner can be designed against. It's needed because `vite.config.ts` keeps the
 * service worker out of dev, and without a service worker Chrome never fires
 * `beforeinstallprompt` — meaning the banner is otherwise unreachable on :5173.
 *
 * Gated on `import.meta.env.DEV`, so the query param does nothing in a
 * production build. The forced state is cosmetic: there's no deferred event
 * behind it, so `promptInstall()` still no-ops.
 */
function readForcedCanInstall() {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('pwaBanner') === '1'
}

/**
 * Captures the deferred `beforeinstallprompt` event app-wide and exposes it via
 * context so install can be offered on OUR terms.
 *
 * The handler calls `preventDefault()` unconditionally, which suppresses the
 * browser's automatic install mini-infobar on EVERY page. Nothing auto-prompts;
 * a prompt appears only when a surface calls `promptInstall()`. Because the
 * pre-qualification screens never render an install affordance, that flow stays
 * prompt-free — install is only offered from the login screen onward.
 *
 * Also owns the two reasons NOT to offer install at all: the app is already
 * running installed, or the user closed the banner earlier this session.
 */
export function PwaInstallProvider({ children }: { children: React.ReactNode }) {
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null)
  const [canInstall, setCanInstall] = useState(readForcedCanInstall)
  // Seeded from the display mode so an already-installed app never offers install,
  // even on iOS where `beforeinstallprompt`/`appinstalled` never fire.
  const [installed, setInstalled] = useState(isRunningStandalone)
  const [dismissed, setDismissed] = useState(readDismissed)

  useEffect(() => {
    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault()
      deferredRef.current = event as BeforeInstallPromptEvent
      setCanInstall(true)
    }
    function onAppInstalled() {
      deferredRef.current = null
      setCanInstall(false)
      setInstalled(true)
    }
    // The tab that launched the install can switch into standalone mode without a
    // reload; track it so the banner disappears the moment the app is installed.
    const standaloneQuery = window.matchMedia('(display-mode: standalone)')
    function onDisplayModeChange(event: MediaQueryListEvent) {
      if (event.matches) setInstalled(true)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)
    standaloneQuery.addEventListener('change', onDisplayModeChange)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
      standaloneQuery.removeEventListener('change', onDisplayModeChange)
    }
  }, [])

  const dismiss = useCallback(() => {
    setDismissed(true)
    try {
      sessionStorage.setItem(PWA_DISMISSED_KEY, '1')
    } catch {
      // Storage unavailable — the banner still stays hidden for this render tree.
    }
  }, [])

  const promptInstall = useCallback(async () => {
    const deferred = deferredRef.current
    if (!deferred) return
    await deferred.prompt()
    // A deferred prompt is single-use; drop it regardless of the user's choice.
    deferredRef.current = null
    setCanInstall(false)
  }, [])

  const value = useMemo<PwaInstallValue>(
    () => ({ canInstall, installed, dismissed, dismiss, promptInstall }),
    [canInstall, installed, dismissed, dismiss, promptInstall],
  )

  return <PwaInstallContext.Provider value={value}>{children}</PwaInstallContext.Provider>
}
